import { useEffect, useState } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { useDocumentTitle } from '../../hooks/useDocumentTitle'
import { supabase } from '../../lib/supabase'
import { ExamAttempt, Certificate, Exam } from '../../lib/types'
import { MainLayout } from '../../components/layouts/MainLayout'
import { Loading } from '../../components/common/Loading'
import { ErrorMessage } from '../../components/common/ErrorMessage'
import { CheckCircle, XCircle, Clock, Award, RefreshCw, FileText } from 'lucide-react'

export function ExamResultPage() {
  useDocumentTitle('試験結果')
  const { attemptId } = useParams<{ attemptId: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const { user } = useAuth()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [attempt, setAttempt] = useState<ExamAttempt | null>(null)
  const [exam, setExam] = useState<Exam | null>(null)
  const [certificate, setCertificate] = useState<Certificate | null>(null)
  const isTimeout = location.state?.timeout || false

  useEffect(() => {
    if (!user || !attemptId) return
    fetchExamResult()
  }, [attemptId, user])

  const fetchExamResult = async () => {
    if (!attemptId || !user) return

    try {
      setLoading(true)
      setError(null)

      // 試験attempt情報を取得
      const { data: attemptData, error: attemptError } = await supabase
        .from('exam_attempts')
        .select('*, exams(*)')
        .eq('attempt_id', attemptId)
        .eq('user_id', user.id)
        .single()

      if (attemptError || !attemptData) {
        setError('試験結果が見つかりませんでした')
        setLoading(false)
        return
      }

      setAttempt(attemptData)
      setExam(attemptData.exams as any)

      // 合格している場合、認定証を取得
      if (attemptData.passed) {
        const { data: certData } = await supabase
          .from('certificates')
          .select('*')
          .eq('attempt_id', attemptId)
          .single()

        if (certData) {
          setCertificate(certData)
        }
      }
    } catch (err: any) {
      console.error('Error fetching exam result:', err)
      setError(err.message || 'データの取得に失敗しました')
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <MainLayout>
        <Loading message="試験結果を読み込み中..." />
      </MainLayout>
    )
  }

  if (error || !attempt || !exam) {
    return (
      <MainLayout>
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <ErrorMessage message={error || '試験結果が見つかりませんでした'} />
        </div>
      </MainLayout>
    )
  }

  // 結果が未公開の場合
  if (attempt.publish_status === 'pending') {
    return (
      <MainLayout>
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="text-center">
            <Clock className="mx-auto h-16 w-16 text-blue-600 mb-6" />
            <h1 className="text-3xl font-bold text-gray-900 mb-4">試験結果を確認中</h1>
            <p className="text-lg text-gray-600 mb-8">
              試験結果は現在確認中です。
              <br />
              結果が公開されるまでしばらくお待ちください。
            </p>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-8">
              <h2 className="text-lg font-semibold text-blue-900 mb-3">提出情報</h2>
              <div className="space-y-2 text-sm text-blue-800">
                <p>提出日時: {new Date(attempt.submitted_at!).toLocaleString('ja-JP')}</p>
                <p>試験名: {exam.title}</p>
              </div>
            </div>

            <button
              onClick={() => navigate('/mypage')}
              className="px-6 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition font-medium"
            >
              マイページへ戻る
            </button>
          </div>
        </div>
      </MainLayout>
    )
  }

  const isPassed = attempt.passed
  const score = attempt.score || 0
  const passingScore = exam.passing_score

  return (
    <MainLayout>
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* タイムアウト通知 */}
        {isTimeout && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
            <div className="flex items-start gap-3">
              <Clock className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-yellow-800">
                <p className="font-medium">制限時間が終了しました</p>
                <p>試験は自動的に提出されました</p>
              </div>
            </div>
          </div>
        )}

        {/* 結果ヘッダー */}
        <div className="text-center mb-8">
          {isPassed ? (
            <>
              <CheckCircle className="mx-auto h-20 w-20 text-green-600 mb-6" />
              <h1 className="text-4xl font-bold text-gray-900 mb-2">合格</h1>
              <p className="text-lg text-gray-600">おめでとうございます！</p>
            </>
          ) : (
            <>
              <XCircle className="mx-auto h-20 w-20 text-red-600 mb-6" />
              <h1 className="text-4xl font-bold text-gray-900 mb-2">不合格</h1>
              <p className="text-lg text-gray-600">もう一度挑戦してください</p>
            </>
          )}
        </div>

        {/* 試験情報 */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">試験情報</h2>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-gray-600">試験名</span>
              <span className="font-medium">{exam.title}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-600">受験日時</span>
              <span className="font-medium">
                {new Date(attempt.started_at).toLocaleString('ja-JP')}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-600">提出日時</span>
              <span className="font-medium">
                {attempt.submitted_at
                  ? new Date(attempt.submitted_at).toLocaleString('ja-JP')
                  : '-'}
              </span>
            </div>
          </div>
        </div>

        {/* 得点 */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">得点</h2>
          <div className="flex items-end justify-center gap-2 mb-4">
            <span className="text-6xl font-bold text-gray-900">{score}</span>
            <span className="text-2xl text-gray-600 pb-2">点</span>
          </div>
          <div className="text-center text-gray-600 mb-4">
            合格点: {passingScore}点
          </div>
          <div className="w-full bg-gray-200 rounded-full h-3">
            <div
              className={`h-3 rounded-full transition-all ${
                isPassed ? 'bg-green-600' : 'bg-red-600'
              }`}
              style={{
                width: `${Math.min(100, (score / passingScore) * 100)}%`,
              }}
            />
          </div>
        </div>

        {/* 合格時: 認定証 */}
        {isPassed && certificate && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-6 mb-6">
            <div className="flex items-start gap-4">
              <Award className="h-6 w-6 text-green-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-green-900 mb-2">
                  認定証が発行されました
                </h3>
                <p className="text-sm text-green-800 mb-4">
                  試験に合格したため、認定証が発行されました。
                  <br />
                  認定証番号: {certificate.certificate_no}
                </p>
                <button
                  onClick={() => navigate(`/certificates/${certificate.certificate_id}`)}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition font-medium"
                >
                  <FileText className="h-4 w-4" />
                  認定証を表示
                </button>
              </div>
            </div>
          </div>
        )}

        {/* 不合格時: 再受験 */}
        {!isPassed && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6">
            <div className="flex items-start gap-4">
              <RefreshCw className="h-6 w-6 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-blue-900 mb-2">
                  再受験について
                </h3>
                <p className="text-sm text-blue-800 mb-4">
                  講座を復習して、再度試験に挑戦してください。
                </p>
                <div className="flex gap-3">
                  <button
                    onClick={() => navigate(`/courses/${attempt.course_id}`)}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-white text-blue-700 border border-blue-300 rounded-md hover:bg-blue-50 transition font-medium"
                  >
                    講座を復習する
                  </button>
                  <button
                    onClick={() => navigate(`/enrollment/retry/${attemptId}`)}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition font-medium"
                  >
                    <RefreshCw className="h-4 w-4" />
                    再受験を申込む
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* アクション */}
        <div className="text-center">
          <button
            onClick={() => navigate('/mypage')}
            className="px-6 py-3 bg-white text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 transition font-medium"
          >
            マイページへ戻る
          </button>
        </div>
      </div>
    </MainLayout>
  )
}
